import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { ENSOMIRAI_API_KEY } from '../services/api';
import { handleUnauthorizedResponse, onUnauthorized } from './authEvents';
import {
  clearSession,
  loadSession,
  saveSession,
  type AuthSession,
  type AuthUser,
} from './session';

interface AuthConfig {
  google_enabled: boolean;
  google_client_id: string;
  dev_login_enabled: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  config: AuthConfig | null;
  loginWithGoogleIdToken: (idToken: string) => Promise<void>;
  loginWithDev: (email: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

/** Paths that must never trigger the "session expired" redirect (they are the auth flow itself). */
const AUTH_FLOW_PATHS = ['/auth/config', '/auth/google', '/auth/dev-login', '/auth/logout'];

async function authFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = (() => {
    try {
      return (localStorage.getItem('enso.auth.token') || '').trim();
    } catch {
      return '';
    }
  })();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    // The session JWT always wins; the automation key is a fallback only.
    ...(token
      ? { Authorization: `Bearer ${token}` }
      : ENSOMIRAI_API_KEY
        ? { 'X-API-Key': ENSOMIRAI_API_KEY }
        : {}),
    ...((init.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!AUTH_FLOW_PATHS.includes(path)) handleUnauthorizedResponse(res.status, path);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    const msg = json?.error?.message || json?.message || res.statusText || 'Auth request failed';
    throw new Error(msg);
  }
  return (json.data ?? json) as T;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(() => loadSession());
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const envClientId = String(import.meta.env.VITE_GOOGLE_CLIENT_ID || '').trim();
    (async () => {
      try {
        const cfg = await authFetch<AuthConfig>('/auth/config');
        if (cancelled) return;
        const clientId = String(cfg.google_client_id || envClientId || '').trim();
        setConfig({
          google_enabled: Boolean(cfg.google_enabled || clientId),
          google_client_id: clientId,
          dev_login_enabled: cfg.dev_login_enabled !== false,
        });
      } catch {
        if (!cancelled) {
          setConfig({
            google_enabled: Boolean(envClientId),
            google_client_id: envClientId,
            dev_login_enabled: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = useCallback((next: AuthSession | null) => {
    if (next) saveSession(next);
    else clearSession();
    setSession(next);
  }, []);

  // Central 401 handling: any API wrapper that sees a 401 clears the stored
  // session and emits this event. Drop the in-memory session, drop every
  // cached query (it belongs to the dead session) and go to /login with the
  // current location as the return path.
  useEffect(() => {
    return onUnauthorized(() => {
      setSession((current) => {
        if (current) queryClient.clear();
        return null;
      });
      if (location.pathname !== '/login') {
        navigate('/login', {
          replace: true,
          state: { from: location.pathname + location.search, reason: 'session_expired' },
        });
      }
    });
  }, [queryClient, navigate, location.pathname, location.search]);

  const loginWithGoogleIdToken = useCallback(
    async (idToken: string) => {
      const data = await authFetch<{ token: string; expires_at?: string; user: AuthUser }>(
        '/auth/google',
        { method: 'POST', body: JSON.stringify({ id_token: idToken }) },
      );
      queryClient.clear();
      applySession({ token: data.token, expiresAt: data.expires_at, user: data.user });
    },
    [applySession, queryClient],
  );

  const loginWithDev = useCallback(
    async (email: string, name: string) => {
      const data = await authFetch<{ token: string; expires_at?: string; user: AuthUser }>(
        '/auth/dev-login',
        { method: 'POST', body: JSON.stringify({ email, name }) },
      );
      queryClient.clear();
      applySession({ token: data.token, expiresAt: data.expires_at, user: data.user });
    },
    [applySession, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch {
      /* ignore */
    }
    applySession(null);
    // The next operator on this tab must not see the previous one's cached data.
    queryClient.clear();
  }, [applySession, queryClient]);

  const refreshMe = useCallback(async () => {
    const token = session?.token || loadSession()?.token;
    if (!token) {
      applySession(null);
      return;
    }
    try {
      const user = await authFetch<AuthUser>('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      applySession({
        token,
        expiresAt: session?.expiresAt || loadSession()?.expiresAt,
        user,
      });
    } catch {
      applySession(null);
    }
  }, [applySession, session?.expiresAt, session?.token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      token: session?.token ?? null,
      loading,
      config,
      loginWithGoogleIdToken,
      loginWithDev,
      logout,
      refreshMe,
    }),
    [session, loading, config, loginWithGoogleIdToken, loginWithDev, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
